import { useState } from "react";
import { Search, Compass, Users, Settings2, RefreshCcw, FileDown, ArrowRight, Check } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

const phaseIcons = [Search, Compass, Users, Settings2, RefreshCcw];

export default function Methodology() {
  const [ref, isVisible] = useScrollReveal();
  const [ref2, isVisible2] = useScrollReveal();
  const { lang, t } = useLanguage();
  const m = t.methodology;
  const lm = m.leadMagnet;

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "framework-download" }),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="methodology" data-testid="methodology-section" ref={ref} className="bg-[#1E3455] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(24px)" }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{m.label}</span>
          </div>
          <h2 className="text-4xl md:text-5xl text-white leading-tight max-w-2xl mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {m.heading}<br />
            <span className="gold-gradient-text">{m.headingAccent}</span>
          </h2>
          <p className="text-slate-300 text-base leading-relaxed max-w-2xl">{m.desc}</p>
        </div>

        {/* Horizontal Phase Stepper */}
        <div className="hidden lg:flex items-center justify-between mb-12 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transitionDelay: "200ms" }}>
          {m.phases.map((phase, i) => (
            <div key={i} className="flex items-center flex-1">
              <button
                onClick={() => {
                  const el = document.querySelector(`[data-testid="methodology-phase-${i}"]`);
                  el?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div className="w-9 h-9 border-2 border-[#D4AF37]/30 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 flex items-center justify-center transition-all duration-300">
                  <span className="text-[#D4AF37] text-xs font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{phase.phase}</span>
                </div>
                <span className="text-slate-400 group-hover:text-[#D4AF37] text-[11px] uppercase tracking-widest transition-colors duration-300 hidden xl:inline">{phase.title.split(" ").slice(0, 2).join(" ")}</span>
              </button>
              {i < m.phases.length - 1 && (
                <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 mx-4" />
              )}
            </div>
          ))}
        </div>

        {/* 5 Phase cards */}
        <div className="space-y-4">
          {m.phases.map((phase, i) => {
            const Icon = phaseIcons[i];
            return (
              <div key={i} data-testid={`methodology-phase-${i}`}
                className="bg-[#0B1221] border border-slate-800 hover:border-[#D4AF37]/30 transition-all duration-500 group relative overflow-hidden"
                style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.5s", transitionDelay: `${i * 100}ms` }}>
                <div className="absolute top-0 left-0 h-full w-1 bg-[#D4AF37]/20 group-hover:bg-[#D4AF37] transition-colors duration-500" />
                <div className="p-8 pl-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-3">
                    <span className="text-[#D4AF37]/30 text-3xl font-bold"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{phase.phase}</span>
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                      <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    <h3 className="text-white text-lg font-semibold mb-2"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{phase.title}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">{phase.desc}</p>
                  </div>
                  <div className="lg:col-span-6">
                    <ul className="space-y-3">
                      {phase.points.map((point, j) => (
                        <li key={j} className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Lead Magnet Card */}
        <div ref={ref2} className="mt-16 relative overflow-hidden"
          style={{ opacity: isVisible2 ? 1 : 0, transform: isVisible2 ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.7s ease-out, transform 0.7s ease-out" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/8 to-[#D4AF37]/3 border border-[#D4AF37]/20" />
          <div className="absolute top-0 left-0 h-full w-1.5 bg-[#D4AF37]" />
          <div className="relative p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="flex-shrink-0 w-14 h-14 bg-[#D4AF37]/15 flex items-center justify-center">
              <FileDown size={24} className="text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 data-testid="lead-magnet-heading" className="text-white text-xl font-semibold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {lm.heading}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">{lm.desc}</p>
            </div>
            <form onSubmit={handleDownload} className="flex-shrink-0 w-full md:w-auto">
              {status === "success" ? (
                <div data-testid="lead-magnet-success" className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 text-[#D4AF37] text-sm font-medium">
                    <Check size={18} />
                    <span>{lm.success}</span>
                  </div>
                  <a
                    href={`/api/download/framework?lang=${lang}`}
                    data-testid="lead-magnet-download-link"
                    className="bg-[#D4AF37] text-[#0B1221] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#F59E0B] transition-colors duration-300 flex items-center gap-2"
                  >
                    <FileDown size={16} />
                    {lm.downloadNow}
                  </a>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="lead-magnet-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={lm.placeholder}
                    className="bg-[#0B1221] border border-slate-700 text-white text-sm px-4 py-3 w-full sm:w-64 focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-slate-600"
                  />
                  <button
                    data-testid="lead-magnet-submit"
                    type="submit"
                    disabled={status === "sending"}
                    className="bg-[#D4AF37] text-[#0B1221] px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#F59E0B] transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-60"
                  >
                    {status === "sending" ? lm.sending : lm.cta}
                    {status !== "sending" && <ArrowRight size={14} />}
                  </button>
                </div>
              )}
              {status === "error" && (
                <p data-testid="lead-magnet-error" className="text-red-400 text-xs mt-2">{lm.error}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
